import { useContext, useEffect, useState } from "react";
import { DrawerContext, UserContext, AuthContext } from "@CONTEXTS";
import {
  Form,
  Input,
  Select,
  Button,
  TreeSelect,
  Spin,
  message,
  Switch,
  Popconfirm,
} from "antd";
import { useGET } from "@API2";
import { axios } from "@LIBS";
import { Constants } from "@CONFIG";
import { AddressEndpoints } from "@ADDRESS";

const { SHOW_CHILD } = TreeSelect;
const { Option } = Select;

const DeliveryDetail = () => {
  const [form] = Form.useForm();
  const { data, setClose } = useContext(DrawerContext);
  const { info } = useContext(AuthContext);
  const twoFactorEnabled = process.env.REACT_APP_2FA_ENABLED;
  const { data: warehousesData, loading: warehousesLoading } = useGET(
    `${AddressEndpoints.warehouses}`,
    true
  );
  const { mutate } = useContext(UserContext);
  const { data: authorities, loading: authoritiesLoading } = useGET(
    "api/options/authorities"
  );
  const [loading, setLoading] = useState(false);
  const [is2FAReset, setIs2FAReset] = useState(false);
  const [role, setRole] = useState(null);
  const [treeData, setTreeData] = useState([]);
  const [warehousesTreeData, setWarehousesTreeData] = useState([]);
  const canReset2FA = data?.isEmailVerified || data?.isPhoneNumberVerified;

  useEffect(() => {
    setIs2FAReset(false);

    if (!authoritiesLoading) {
      if (data?.id) {
        form.setFieldsValue({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          role: data.role,
          status: data.meta.status,
          warehouses: data.warehouses || [],
          onlySavedReceiversDeliveries:
            data.onlySavedReceiversDeliveries || false,
          authorities: data.authorities.reduce((prev, next) => {
            const that = authorities.find((x) => x.key === next);
            if (that?.parent) {
              prev.push(next);
            }
            return prev;
          }, []),
        });
        setRole(data.role);
      }
      const tree = authorities
        .filter((x) => !x.parent)
        .map((item) => ({
          title: item.name,
          value: item.key,
          key: item.key,
          children: authorities
            .filter((y) => y.parent === item.key)
            .map((z) => ({
              title: z.name,
              value: z.key,
              key: z.key,
            })),
        }));
      setTreeData(tree);
    }
    if (!warehousesLoading) {
      const warehouseTree = warehousesData.map((item) => ({
        title: item.warehouseName,
        value: item.warehouseId,
        key: item.warehouseId,
      }));
      setWarehousesTreeData(warehouseTree);
    }
  }, [
    data,
    form,
    authorities,
    authoritiesLoading,
    warehousesData,
    warehousesLoading,
  ]);

  const handleSave = () => {
    form.validateFields().then(async (values) => {
      const arr = [];
      if (values.authorities) {
        values.authorities.forEach((item) => {
          // parentı bul ve daha önce eklenmemişse pushla.
          const that = authorities.find((x) => x.key === item);
          const parent = authorities.find((x) => x.key === that.parent);
          if (parent && !arr.includes(parent.key)) {
            arr.push(parent.key);
          }
          arr.push(item);
        });
      }

      setLoading(true);

      let response = null;
      if (data?.id) {
        response = await axios({
          url: `${Constants.BACKEND_API_URL}/api/admin/users?id=${data?.id}`,
          method: "PUT",
          data: {
            ...values,
            authorities: arr,
          },
        });
      } else {
        response = await axios({
          url: `${Constants.BACKEND_API_URL}/api/admin/users`,
          method: "POST",
          data: {
            ...values,
            authorities: arr,
          },
        });
      }

      if (response?.success) {
        mutate();
        setTimeout(() => {
          setClose();
        }, 750);
      } else {
        message.error({
          content: response.response.data.error.msg,
          className: "app-error-message",
        });
      }
      setLoading(false);
    });
  };

  const handleReset2FA = async () => {
    setLoading(true);

    const response = await axios({
      url: `${Constants.BACKEND_API_URL}/api/admin/users/reset-2fa?id=${data?.id}`,
      method: "PUT",
    });

    if (response?.success) {
      message.success({
        content: response.message,
        className: "app-success-message",
      });
      setIs2FAReset(true);
      mutate();
    } else {
      message.error({
        content:
          response?.response?.data?.error?.msg ||
          response?.message ||
          "2 aşamalı doğrulama sıfırlanırken bir hata oluştu.",
        className: "app-error-message",
      });
    }

    setLoading(false);
  };

  return (
    <Spin spinning={loading}>
      <Form layout="vertical" form={form}>
        <Form.Item name="firstName" label="Adı">
          <Input />
        </Form.Item>
        <Form.Item name="lastName" label="Soyadı">
          <Input />
        </Form.Item>
        <Form.Item
          name="email"
          label="E-Posta"
          rules={[
            { type: "email", message: "Geçerli bir e-posta giriniz" },
            { required: true, message: "geçerli bir e-posta giriniz." },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="password"
          label="Şifre"
          rules={[
            {
              required: data?.id ? false : true,
              message: "geçerli bir e-posta giriniz.",
            },
            () => ({
              validator(_, value) {
                if (!data?.id && !value) {
                  return Promise.reject();
                }
                if (value && value.length < 8) {
                  return Promise.reject("Şifre en az 8 haneli olmalı!");
                }
                if (value && !/[a-z]/.test(value)) {
                  return Promise.reject("Şifre en az bir küçük harf içermeli!");
                }
                if (value && !/[A-Z]/.test(value)) {
                  return Promise.reject("Şifre en az bir büyük harf içermeli!");
                }
                if (value && !/[0-9]/.test(value)) {
                  return Promise.reject("Şifre en az bir rakam içermeli!");
                }
                if (
                  value &&
                  !/[!@#$%^&*(),.":{}|<>_\-+=\[\]\\\/]/.test(value)
                ) {
                  return Promise.reject(
                    "Şifre en az bir özel karakter içermeli!"
                  );
                }
                if (value && /\$/.test(value)) {
                  return Promise.reject("Şifre $ işareti içeremez!");
                }
                if (value && /\s/.test(value)) {
                  return Promise.reject("Şifre boşluk içeremez!");
                }

                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item
          name="role"
          label="Rol"
          rules={[{ required: true, message: "Rol seçiniz." }]}
        >
          <Select onChange={(val) => setRole(val)}>
            <Option value="ADMIN">TAM YETKİLİ KULLANICI</Option>
            <Option value="USER">KULLANICI</Option>
          </Select>
        </Form.Item>
        {role === "USER" && (
          <Form.Item
            name="authorities"
            label="Yetkiler"
            rules={[{ required: true, message: "Yetki seçiniz." }]}
          >
            <TreeSelect
              treeCheckable
              showCheckedStrategy={SHOW_CHILD}
              placeholder="Yetki seçiniz"
              treeData={treeData}
              multiple
            />
          </Form.Item>
        )}
        {role === "USER" && (
          <Form.Item name="warehouses" label="Depolar">
            <TreeSelect
              treeCheckable
              placeholder="Depo seçiniz"
              treeData={warehousesTreeData}
              multiple
              showSearch
              filterTreeNode={(input, treeNode) =>
                treeNode.title.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        )}
        {role === "USER" && (
          <Form.Item
            name="onlySavedReceiversDeliveries"
            label="Manuel Alıcı Kaydı Girilmesin"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        )}

        <Form.Item
          name="status"
          label="Durum"
          rules={[{ required: true, message: "Durum seçiniz." }]}
        >
          <Select>
            <Option value="ACTIVE">Aktif</Option>
            <Option value="PASSIVE">Pasif</Option>
            <Option value="DELETED">Silinmiş</Option>
          </Select>
        </Form.Item>
        {data?.id && twoFactorEnabled === "true" && info?.twoFactorEnabled && (
          <Form.Item>
            <Popconfirm
              title="2 Aşamalı Doğrulamayı Sıfırla"
              description="Kullanıcının 2 aşamalı doğrulaması sıfırlanacak. Emin misiniz?"
              okText="Evet"
              cancelText="Vazgeç"
              disabled={!canReset2FA || is2FAReset}
              onConfirm={() => handleReset2FA()}
            >
              <Button danger block disabled={!canReset2FA || is2FAReset}>
                2 Aşamalı Doğrulamayı Sıfırla
              </Button>
            </Popconfirm>
          </Form.Item>
        )}
        <Form.Item>
          <Button
            type="primary"
            style={{ float: "right" }}
            onClick={() => handleSave()}
          >
            Kaydet
          </Button>
        </Form.Item>
      </Form>
    </Spin>
  );
};
export default DeliveryDetail;
